/* @jsx h */
import { defineComponent, ref, h, Ref, computed } from "vue";
import {
  useLocalization,
  FullScreenSheet,
  type FullScreenSheetSlotProps,
} from "@lemmonhq/uikit";
import { CartAPI } from "@lemmonhq/orders";
import {
  Cart,
  CartPersona,
  FoodType,
  Product,
  TableStatus,
} from "@lemmonhq/types";
import { CartItems } from "../CartItems";
import { calculateCartSubtotal } from "@lemmonhq/orders";
import { useCheckout } from "@app/vue/providers/Checkout";
import { useRouter } from "vue-router";
import { useAuth } from "@app/vue/providers/Auth";
import {
  getMobilePosOrderTypeOptions,
  getEffectiveMobilePosOrderTypesFromSettings,
  type MobilePosOrderType,
} from "../../../shared/mobilePosOrderTypes";

export type POSMobileCheckoutSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  cart: Cart;
  cartAPI: CartAPI;
  onClear: Function;
  isNotesModalOpen: Ref<boolean>;
  isTableSelectModalOpen: Ref<boolean>;
  selectedTable?: TableStatus;
  customers: CartPersona[];
  foodTypes: FoodType[];
  products: Product[];
};

export const POSMobileCheckoutSheet =
  defineComponent<POSMobileCheckoutSheetProps>({
    props: [
      "isOpen",
      "onClose",
      "cart",
      "cartAPI",
      "onClear",
      "isNotesModalOpen",
      "isTableSelectModalOpen",
      "selectedTable",
      "customers",
      "foodTypes",
      "products",
    ],
    setup(props) {
      const activeOrderType = ref<MobilePosOrderType | null>(null);
      const checkout = useCheckout();
      const router = useRouter();
      const auth = useAuth();
      const { t } = useLocalization();

      const availableOrderTypes = computed(() => {
        const featureFlags = auth.user.value?.restaurant.feature_flags || {};
        if (featureFlags.mobile_pos_only_dine_in) {
          return getMobilePosOrderTypeOptions(["table"]);
        }
        return getMobilePosOrderTypeOptions(
          getEffectiveMobilePosOrderTypesFromSettings(
            auth.user.value?.restaurant_settings,
          ),
        );
      });

      const handleOrderType = (type: MobilePosOrderType) => {
        if (type === "table") {
          props.isTableSelectModalOpen.value = true;
          props.onClose();
        } else {
          activeOrderType.value = type;
          checkout.setCheckout({
            payload: { type: "cart", object: props.cart },
            serviceMethod: type,
          });
          checkout.show({
            onCancel() {
              activeOrderType.value = null;
            },
            onSuccess() {
              activeOrderType.value = null;
              props.onClear();
              router.push("/app/pos");
              props.onClose();
            },
          });
        }
      };

      return () => {
        if (!props.isOpen) return null;
        return (
          <FullScreenSheet isOpen={props.isOpen} onClose={props.onClose}>
            {{
              default: ({ close }: FullScreenSheetSlotProps) => (
                <div
                  style={{
                    paddingTop: `var(--safe-area-top, 0px)`,
                  }}
                  class="flex flex-col h-full w-full bg-base-100"
                >
                  {/* Header */}
                  <div class="flex items-center px-4 py-3 border-b ">
                    <button
                      class="btn btn-ghost btn-sm btn-circle mr-2"
                      onClick={close}
                    >
                      <span class="material-symbols-outlined">arrow_back</span>
                    </button>
                    <div class="flex-1 text-center font-semibold text-lg text-accent">
                      {t("general.words.checkout")}
                    </div>
                    <button
                      onClick={() => {
                        props.onClear();
                        close();
                      }}
                      class="btn btn-ghost btn-sm btn-circle text-error"
                    >
                      <span class="material-symbols-outlined">delete</span>
                    </button>
                  </div>

                  {/* Scrollable body */}
                  <div class="flex-1 overflow-y-auto pb-4">
                    <div class="p-4">
                      {props.selectedTable && (
                        <div class="flex items-center gap-1 text-accent mb-3">
                          <span class="material-symbols-outlined text-xl">
                            table_restaurant
                          </span>
                          <span class="font-medium">
                            {props.selectedTable.table.name}
                          </span>
                        </div>
                      )}
                      <div class="bg-base-200 rounded-2xl p-3 l-box-shadow">
                        <CartItems
                          cartAPI={props.cartAPI}
                          items={props.cart.items}
                          onIncrement={props.cartAPI.incrementQuantity}
                          onDecrement={props.cartAPI.decrementQuantity}
                          customers={props.cart.customers}
                          products={props.products}
                          foodTypes={props.foodTypes}
                        />
                        <div class="pt-2">
                          <button
                            class="btn btn-neutral btn-block"
                            onClick={() =>
                              (props.isNotesModalOpen.value = true)
                            }
                          >
                            <span class="material-symbols-outlined mr-2">
                              comment
                            </span>
                            {t("pos.pay.add_notes")}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Totals */}
                    <div class="px-4 pt-2 pb-2">
                      <div class="flex items-center justify-between py-3 border-t ">
                        <span class="text-accent text-sm">
                          {t("pos.pay.total_amount")}
                        </span>
                        <span class="font-semibold text-accent">
                          {auth.user.value?.restaurant.currency || "CHF"}{" "}
                          {calculateCartSubtotal(props.cart).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Order type buttons — fixed to bottom for easy one-hand reach */}
                  <div class="border-t  bg-base-100 px-4 pt-3 pb-safe-bottom safe-bottom">
                    <p class="text-xs text-base-content/50 text-center mb-3 font-medium uppercase tracking-wide">
                      {t("pos.cart.place_order_button")}
                    </p>
                    <div class="flex gap-2 pb-4">
                      {availableOrderTypes.value.map((type) => {
                        const isActive = activeOrderType.value === type.value;
                        return (
                          <button
                            style="box-shadow: none;"
                            key={type.value}
                            class={`flex flex-col flex-1 items-center justify-center gap-0.5 py-2 btn ${type.btnClass} border-0 min-h-0 h-auto`}
                            disabled={
                              activeOrderType.value !== null || isActive
                            }
                            onClick={() => handleOrderType(type.value)}
                          >
                            <span class="text-xs font-semibold leading-tight">
                              {t(type.labelKey)}
                            </span>
                            <span class="material-symbols-outlined text-2xl">
                              {type.icon}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ),
            }}
          </FullScreenSheet>
        );
      };
    },
  });
